import type { HTMLAttributes, Ref } from 'react';

import { ImageOff, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import type { FileQueryKey, FileQueryOptions } from '../hooks/use-file';

import { useImage } from '../hooks/use-image';
import { cn } from '../lib/utils';
import {
    Popout,
    PopoutBody,
    PopoutContent,
} from './popout';
import { Skeleton } from './skeleton';

export type ImageProps<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey> = Omit<HTMLAttributes<HTMLImageElement>, 'src'> & {
    queryOptions: FileQueryOptions<TData, TError, TQueryKey>;
    alt: string;
    enabled?: boolean;
    ref?: Ref<HTMLImageElement>;
    previewer?: boolean;
};

export function Image<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey>(props: ImageProps<TData, TError, TQueryKey>) {
    const { queryOptions, alt, enabled = true, ref, previewer = false, className, ...restProps } = props;

    const [open, setOpen] = useState(false);

    const { url, urlState, fileState } = useImage({ queryOptions, enabled });

    const isLoading = useMemo(() => {
        if (fileState.isLoading)
            return true;
        if (urlState.isLoading)
            return true;
        return false;
    }, [fileState.isLoading, urlState.isLoading]);

    const isError = useMemo(() => {
        if (fileState.isError)
            return true;
        if (urlState.isError)
            return true;
        if (!url)
            return true;
        return false;
    }, [fileState.isError, url, urlState.isError]);

    if (!enabled) {
        return (
            <Skeleton className={cn('w-full h-full flex items-center justify-center', className)}>
                <ImageOff size={24} />
            </Skeleton>
        );
    }

    if (isLoading || isError) {
        return (
            <Skeleton className={cn('w-full h-full flex items-center justify-center', className)}>
                <Loader2 size={24} className="animate-spin" />
            </Skeleton>
        );
    }

    const imageElement = (
        <img
            ref={ref}
            src={url ?? undefined}
            alt={alt}
            className={cn(previewer && 'cursor-pointer', className)}
            onClick={previewer ? () => setOpen(true) : undefined}
            {...restProps}
        />
    );

    if (!previewer) {
        return imageElement;
    }

    return (
        <>
            {imageElement}
            <Popout open={open} onOpenChange={setOpen}>
                <PopoutContent className="max-h-screen !rounded-none sm:w-full !p-0">
                    <PopoutBody className="h-full w-full">
                        <TransformWrapper
                            smooth={true}
                            initialScale={1}
                            minScale={0.5}
                            maxScale={5}
                            wheel={{ step: 0.2 }}
                            pinch={{ step: 5 }}
                        >
                            <TransformComponent>
                                <img
                                    src={url ?? undefined}
                                    alt={alt}
                                    className="h-full w-full max-h-[70vh] max-w-[80vw] flex justify-center"
                                />
                            </TransformComponent>
                        </TransformWrapper>
                    </PopoutBody>
                </PopoutContent>
            </Popout>
        </>
    );
}
